import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { BillRun } from '../api/types';
import { Card, Badge, Button, Alert, DataTable } from '../components/ui';
import type { DataTableColumn } from '../components/ui';
import { billRunStatusTone } from '../lib/billRunStatusTone';
import { fmtCurrencyOrDash } from '../lib/fmt';

// Both dashboard lists fetch more rows than they display up front; this is
// how many show before the "Show more" control reveals the rest of the
// already-fetched array (see the loadDashboardData comment below for why
// there's no server-side page size to lean on instead).
const DASHBOARD_PREVIEW_COUNT = 5;

export default function Dashboard() {
  const [pendingApprovals, setPendingApprovals] = useState<BillRun[]>([]);
  const [recentRuns, setRecentRuns] = useState<BillRun[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    totalIsFloor: false,
    pending: 0,
    pendingIsFloor: false,
    approved: 0,
    approvedIsFloor: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllPending, setShowAllPending] = useState(false);
  const [showAllRecent, setShowAllRecent] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // ---------------------------------------------------------------------
  // Upstream limitation (out of scope to fix here): `GET /api/bill-runs` in
  // workers/billing-api reads `status` but ignores `limit`/`page` entirely —
  // it always ends with a hardcoded `ORDER BY br.created_at DESC LIMIT 100`.
  // The API fix is deliberately deferred (shared multi-tenant production,
  // manual `wrangler deploy`). Consequences handled on the client instead:
  //   - `api.getBillRuns({ limit: N })` returns up to 100 rows no matter
  //     what N is, so both lists below are sliced client-side to a 5-row
  //     preview with a "Show more" control that reveals the rest of the
  //     *already-fetched* array — no follow-up request, since paging isn't
  //     supported server-side.
  //   - `stats.total` is really `min(actual total in the realm, 100)`. Past
  //     100 bill runs in a realm this would silently freeze at 100 forever,
  //     so when the response is exactly 100 rows the tile renders "100+"
  //     instead of "100" — admitting the floor rather than hiding it.
  //   - `stats.pending` is derived from `pendingApprovals` (the result of
  //     the dedicated `status: 'pending_approval'` query) rather than by
  //     re-filtering `recentRuns`, so there's one source of truth instead of
  //     two independent counts that can disagree.
  //   - I8: `pendingRes` hits the exact same hardcoded-LIMIT-100 endpoint as
  //     `recentRes` — `limit` is ignored regardless of which `status` filter
  //     is passed — so `stats.pending` can silently freeze at 100 forever
  //     too. `pendingIsFloor` admits it the same way `totalIsFloor` already
  //     does for `stats.total`.
  //   - `stats.approved` used to be `recentRes.data.filter(approved ||
  //     finalized).length` — i.e. "approved among the 100 most recent runs",
  //     a silently *windowed* count sitting next to two realm-wide,
  //     floor-admitting ones. Past 100 runs in a realm the three tiles were
  //     literally counting different populations: an exact-looking "63" next
  //     to "100+" and "100+", where the realm might hold 400 approved runs.
  //     It now gets its own status-scoped queries so all three tiles mean the
  //     same thing — "realm-wide, capped at the API's 100 rows, with the cap
  //     admitted as `+`". `approved` needs two queries because the API's
  //     `status` filter is a single `br.status = ?` equality and this tile
  //     has always counted both `approved` and `finalized`. They ride along
  //     in the same Promise.all as `recentRes`, so the load still costs two
  //     round trips, not four.
  //   - Even so, the tiles remain *lower bounds*, not a partition: `total` is
  //     itself capped at 100, so with >=100 runs the parts can sum past the
  //     whole. Only the API can fix that — see the runbook note; the honest
  //     server-side change is a `SELECT status, COUNT(*) ... GROUP BY status`
  //     counts endpoint (or `total`/`by_status` alongside `data`), which
  //     would let all three tiles drop the `+` entirely.
  // ---------------------------------------------------------------------
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      setShowAllPending(false);
      setShowAllRecent(false);

      // Load pending approvals.
      // `limit` is passed but currently ignored server-side (see comment
      // above) — kept so the intent is documented for whoever fixes the
      // API, and the client-side slicing below covers the gap meanwhile.
      const pendingRes = await api.getBillRuns({ status: 'pending_approval', limit: 5 });
      setPendingApprovals(pendingRes.data);

      // Load recent runs (same `limit` caveat: the API always returns up to
      // 100 rows regardless of the value passed here) plus the two
      // status-scoped queries that back the Approved tile. In parallel, so
      // making that tile realm-wide costs no extra round trip.
      const [recentRes, approvedRes, finalizedRes] = await Promise.all([
        api.getBillRuns({ limit: 10 }),
        api.getBillRuns({ status: 'approved' }),
        api.getBillRuns({ status: 'finalized' }),
      ]);
      setRecentRuns(recentRes.data);

      // Calculate stats. Every tile is now the same kind of number: a
      // realm-wide count from its own query, capped at the API's hardcoded
      // 100 rows, with the cap admitted as a trailing "+".
      const approvedCount = approvedRes.data.length + finalizedRes.data.length;
      setStats({
        total: recentRes.data.length,
        totalIsFloor: recentRes.data.length === 100,
        pending: pendingRes.data.length,
        pendingIsFloor: pendingRes.data.length === 100,
        approved: approvedCount,
        // Either leg hitting the cap makes the sum a floor, not an exact
        // count — the two queries are capped independently.
        approvedIsFloor: approvedRes.data.length === 100 || finalizedRes.data.length === 100,
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Converged onto the shared formatter (src/lib/fmt.ts), which is where the
  // non-breaking separator after "R" lives — the hand-rolled ASCII space was
  // the only break opportunity in the string and orphaned the "R" onto its own
  // line in narrow table columns.
  const formatCurrency = fmtCurrencyOrDash;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Same status enum as BillRun.status rendered by BillRuns.tsx. This used to
  // be a hand-copied local `STATUS_TONE` literal that *claimed* to reuse
  // BillRuns.tsx's mapping but silently omitted `error`, so a failed bill run
  // fell through the `?? 'neutral'` fallback and showed a grey "nothing to see
  // here" chip on the landing screen while the same run rendered red on
  // /bill-runs. Now imported from src/lib/billRunStatusTone.ts so the claim is
  // enforced by the module system instead of by a comment.
  const getStatusBadge = (status: string) => (
    <Badge tone={billRunStatusTone(status)}>{status.replace('_', ' ')}</Badge>
  );

  // Recent Bill Runs: no selection and no separate rowActions cell (the
  // Period cell IS the link), so at 375px the only extra non-data cell is
  // DataTable's own expand toggle. Status was originally a third primary
  // column here, but demoted to secondary (2026-07-30 mobile Phase V — same
  // lesson BillRunsTable.tsx:107-115 already carries a dated comment for):
  // Badge sets `whitespace-nowrap` unconditionally, so a value like "pending
  // approval" (~17 characters) can't wrap, and combined with DataTable's
  // collapsing-branch overflow fix that pushes the page wide at 375px
  // instead of silently squeezing. Period identifies the row and Total is
  // the number people scan for; Status remains one tap away in the expanded
  // detail, matching every other migrated table in this phase family.
  const recentColumns: DataTableColumn<BillRun>[] = [
    {
      key: 'period',
      label: 'Period',
      priority: 'primary',
      render: (run) => (
        <Link to={`/bill-runs/${run.id}`} className="text-accent hover:opacity-90">
          {formatDate(run.period_start)} - {formatDate(run.period_end)}
        </Link>
      ),
    },
    {
      key: 'bills',
      label: 'Bills',
      priority: 'secondary',
      render: (run) => <span className="text-content-muted">{run.bill_count || 0}</span>,
    },
    {
      key: 'total',
      label: 'Total',
      priority: 'primary',
      numeric: true,
      render: (run) => <span className="font-medium">{formatCurrency(run.total_amount_cents)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      priority: 'secondary',
      render: (run) => getStatusBadge(run.status),
    },
    {
      key: 'created',
      label: 'Created',
      priority: 'secondary',
      render: (run) => <span className="text-content-muted">{formatDate(run.created_at)}</span>,
    },
  ];

  if (loading) {
    return (
      <div className="p-4 lg:p-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 bg-border-token rounded w-48"></div>
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-border-token rounded-xl"></div>
            ))}
          </div>
          <div className="h-64 bg-border-token rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 lg:p-8">
        <Alert tone="danger" className="text-center">
          <p>{error}</p>
          <button
            onClick={loadDashboardData}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-content">Dashboard</h1>
        <Link
          to="/bill-runs"
          className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors"
        >
          Create Bill Run
        </Link>
      </div>

      {/* Stats cards — tabular not needed here: these are plain integer
          counts (bill-run counts), not currency, so no digit-alignment
          concern the way a currency column has. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center gap-4">
            {/* bg-primary-100 is a literal Tailwind colour, not a CSS-var
                token, so it doesn't auto-switch with the theme. Without
                dark:bg-primary-900 the icon (text-accent -> teal-400 in
                dark) would sit on a background that stays teal-100 —
                contrastRatio('#2dd4bf','#ccfbf1') = 1.65:1, near-invisible.
                dark:bg-primary-900 (#134e4a, same hex as Badge's
                success-tone dark fill) against teal-400 is 5.09:1 —
                computed by hand, passes AA. */}
            <div className="w-12 h-12 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <svg className="w-6 h-6 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-content-muted">Total Bill Runs</p>
              {/* "100+" admits the server's hardcoded LIMIT 100 floor instead
                  of silently reporting a wrong exact count — see the
                  loadDashboardData comment above. */}
              <p className="text-2xl font-bold text-content">{stats.totalIsFloor ? `${stats.total}+` : stats.total}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            {/* yellow-100/yellow-600 is a literal fixed pair (unchanged by
                theme either side) so its own internal contrast doesn't
                regress in dark mode — but left standing alone at full
                pastel brightness it reads as a jarring light disc against
                the new dark Card, the same "looks broken in dark mode"
                failure mode this sweep targets, just not a hard AA
                violation. dark:bg-yellow-900/dark:text-yellow-300 matches
                the darkened treatment given to the other two tiles;
                contrastRatio(yellow-300 #fde047, yellow-900 #713f12)
                computed by hand = 6.58:1, comfortably passes. */}
            <div className="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center">
              <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-content-muted">Pending Approval</p>
              {/* I8: same floor admission as the Total Bill Runs tile above
                  — `pendingRes` hits the identical hardcoded-LIMIT-100
                  endpoint, so this can silently freeze at 100 too. */}
              <p className="text-2xl font-bold text-content">{stats.pendingIsFloor ? `${stats.pending}+` : stats.pending}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-4">
            {/* Same treatment as the Pending tile above — green-100/green-600
                is a fixed pair, dark:bg-green-900/dark:text-green-300 keeps
                it from popping as a bright disc on the dark Card.
                contrastRatio(green-300 #86efac, green-900 #14532d)
                computed by hand = 6.49:1, passes. */}
            <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600 dark:text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-sm text-content-muted">Approved</p>
              {/* Same floor admission as the two tiles above, for the same
                  reason — this count now comes from its own status-scoped
                  queries against the identical hardcoded-LIMIT-100 endpoint,
                  so all three tiles are realm-wide capped counts rather than
                  two realm-wide ones next to a silently windowed one. */}
              <p className="text-2xl font-bold text-content">{stats.approvedIsFloor ? `${stats.approved}+` : stats.approved}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Pending Approvals */}
      {pendingApprovals.length > 0 && (
        <Card className="shadow-sm mb-8">
          <div className="px-6 py-4 border-b border-border-token">
            <h2 className="text-lg font-semibold text-content">Pending Approvals</h2>
          </div>
          <div className="divide-y divide-border-token">
            {(showAllPending ? pendingApprovals : pendingApprovals.slice(0, DASHBOARD_PREVIEW_COUNT)).map((run) => (
              <Link
                key={run.id}
                to={`/bill-runs/${run.id}`}
                className="flex items-center justify-between px-6 py-4 hover:bg-surface-sunken transition-colors"
              >
                <div>
                  <p className="font-medium text-content">
                    {formatDate(run.period_start)} - {formatDate(run.period_end)}
                  </p>
                  <p className="text-sm text-content-muted">
                    {run.bill_count || 0} bills | Created {formatDate(run.created_at)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-content tabular">{formatCurrency(run.total_amount_cents)}</p>
                  {getStatusBadge(run.status)}
                </div>
              </Link>
            ))}
          </div>
          {pendingApprovals.length > DASHBOARD_PREVIEW_COUNT && (
            <div className="px-6 py-3 border-t border-border-token flex justify-center">
              <Button variant="ghost" size="sm" onClick={() => setShowAllPending((v) => !v)}>
                {showAllPending
                  ? 'Show fewer'
                  : `Show ${pendingApprovals.length - DASHBOARD_PREVIEW_COUNT} more`}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Recent Bill Runs */}
      <Card className="shadow-sm">
        <div className="px-6 py-4 border-b border-border-token flex items-center justify-between">
          <h2 className="text-lg font-semibold text-content">Recent Bill Runs</h2>
          <Link to="/bill-runs" className="text-sm text-accent hover:opacity-90">
            View all
          </Link>
        </div>
        {recentRuns.length === 0 ? (
          <div className="px-6 py-12 text-center text-content-muted">
            <p>No bill runs yet</p>
            <Link
              to="/bill-runs"
              className="inline-block mt-4 text-accent hover:opacity-90"
            >
              Create your first bill run
            </Link>
          </div>
        ) : (
          <div>
            <DataTable
              data={showAllRecent ? recentRuns : recentRuns.slice(0, DASHBOARD_PREVIEW_COUNT)}
              columns={recentColumns}
              rowKey={(run) => run.id}
              density="comfortable"
            />
            {recentRuns.length > DASHBOARD_PREVIEW_COUNT && (
              <div className="px-6 py-3 border-t border-border-token flex justify-center">
                <Button variant="ghost" size="sm" onClick={() => setShowAllRecent((v) => !v)}>
                  {showAllRecent
                    ? 'Show fewer'
                    : `Show ${recentRuns.length - DASHBOARD_PREVIEW_COUNT} more`}
                </Button>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
